import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Typography,
  Container,
  Paper,
  Grid,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Stepper,
  Step,
  StepLabel,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Divider,
  InputAdornment,
  CircularProgress,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Chip,
  Alert
} from "@mui/material";
import {
  CloudUpload,
  AttachFile,
  Delete,
  InsertDriveFile,
  PictureAsPdf,
  Description as DocIcon,
} from "@mui/icons-material";
import {
  ArrowBack,
  KeyboardArrowRight,
  Check,
  Description,
  CalendarToday,
  Add,
  AutoAwesome,
  TrendingUp,
  WarningAmber
} from "@mui/icons-material";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Main component
export default function ProjectManagementFlow() {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState("templateSelection");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [projectType, setProjectType] = useState(null);
  const [projectData, setProjectData] = useState({
    basicInfo: {
      name: "",
      key: "",
      projectType: "",
      description: "",
      costCenterId: "",
      projectManagerId: "",
      productManagerId: "",
      productOwnerId: ""
    },
    timeline: {
      startDate: "",
      endDate: "",
      budget: "",
      bufferHours: ""
    }
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [projectId, setProjectId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadError, setUploadError] = useState("");
  const [employeeId, setEmployeeId] = useState(null);
  const [error, setError] = useState(null);
  const [npiSource, setNpiSource] = useState(null);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const editMode = queryParams.get("edit") === "true";
    const id = queryParams.get("id");
    
    if (editMode && id) {
      setIsEditMode(true);
      setProjectId(id);
      loadProjectData(id);
    } else if (location.state?.npiData) {
      const npi = location.state.npiData;
      setNpiSource(npi);

      const defaultTemplate = {
        id: "scrum",
        title: "SCRUM",
        subtitle: "Iterative development with time-boxed sprints",
        description: "Organize work into fixed-length sprints with planning, daily standups, and retrospectives to continuously improve.",
        color: "#b2dfdb",
        bgColor: "#009688",
        icon: "🔄"
      };
      setSelectedTemplate(defaultTemplate);
      setProjectType("company");

      let formattedStartDate = "";
      let formattedEndDate = "";
      if (npi.proposed_start_date) {
        try {
          formattedStartDate = new Date(npi.proposed_start_date).toISOString().split('T')[0];
        } catch (e) {}
      }
      if (npi.proposed_end_date) {
        try {
          formattedEndDate = new Date(npi.proposed_end_date).toISOString().split('T')[0];
        } catch (e) {}
      }

      let suggestedKey = "";
      if (npi.title) {
        const words = npi.title.replace(/[^a-zA-Z0-9 ]/g, "").trim().split(/\s+/);
        for (let i = 0; i < Math.min(words.length, 3); i++) {
          if (words[i]) suggestedKey += words[i][0].toUpperCase();
        }
        if (suggestedKey.length < 3) suggestedKey += "PRJ";
      }

      setProjectData(prev => ({
        basicInfo: {
          ...prev.basicInfo,
          name: npi.title || "",
          key: suggestedKey || "PRJ",
          projectType: npi.category || "Software",
          description: npi.business_case || ""
        },
        timeline: {
          startDate: formattedStartDate || new Date().toISOString().split('T')[0],
          endDate: formattedEndDate || "",
          budget: npi.estimated_budget ? String(npi.estimated_budget) : ""
        }
      }));

      setCurrentPage("projectForm");
    }
  }, [location]);


    const getEmployeeid = async () => {
    try {
       const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, {
        withCredentials: true
      });

      if (!response.data || !response.data.employee_id) {
              alert("session Expired. Please log in.");

        throw new Error('Employee ID not found in user data');
      }

      setEmployeeId(response.data.employee_id);
      
      return response.data.employee_id;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      setError('Failed to fetch user information');
      return null;
    }
  };



  
  const loadProjectData = async (id) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      let projectToEdit = null;

      if (token) {
        try {
          const response = await axios.get(`${API_URL}/api/projects/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            }
          });
          if (response.data.status === "success") {
            projectToEdit = response.data.data;
          }
        } catch (apiErr) {
          console.warn("Could not fetch project from API, checking localStorage fallback:", apiErr);
        }
      }

      if (!projectToEdit) {
        const storedProject = localStorage.getItem('editProject');
        if (storedProject) {
          projectToEdit = JSON.parse(storedProject);
        }
      }

      if (projectToEdit) {
        const templateType = determineTemplateType(projectToEdit);
        setSelectedTemplate(templateType);
        
        const projType = projectToEdit.projectManagement === "Team-managed project" ? "team" : "company";
        setProjectType(projType);
        
        let formattedStartDate = formatDateForInput(projectToEdit.startDate || projectToEdit.start_date);
        let formattedEndDate = formatDateForInput(projectToEdit.endDate || projectToEdit.end_date);
        const budget = projectToEdit.budget ? String(projectToEdit.budget) : "";
        
        setProjectData({
          basicInfo: {
            name: projectToEdit.name || "",
            key: projectToEdit.key || "",
            projectType: projectToEdit.type || projectToEdit.projectType || "",
            description: projectToEdit.description || "",
            costCenterId: projectToEdit.costCenterId || projectToEdit.cost_center_id || "",
            projectManagerId: projectToEdit.manager_id || projectToEdit.projectManagerId || projectToEdit.project_manager_id || "",
            productManagerId: projectToEdit.product_manager_id || projectToEdit.productManagerId || "",
            productOwnerId: projectToEdit.product_owner_id || projectToEdit.productOwnerId || projectToEdit.lead_id || ""
          },
          timeline: {
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            budget: budget,
            originalBudget: projectToEdit.original_budget ? String(projectToEdit.original_budget) : budget,
            revisedBudget: projectToEdit.revised_budget ? String(projectToEdit.revised_budget) : "",
            bufferHours: projectToEdit.buffer_hours ? String(projectToEdit.buffer_hours) : (projectToEdit.bufferHours ? String(projectToEdit.bufferHours) : ""),
            reason: projectToEdit.budget_revision_reason || projectToEdit.reason || "",
            notes: projectToEdit.notes || ""
          },
          hoursMetrics: projectToEdit.hoursMetrics || null
        });
        
        setCurrentPage("projectForm");
      } else {
        alert("Project data could not be found.");
      }
    } catch (error) {
      //console.error("Error loading project data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const determineTemplateType = (project) => {
    const templateMap = {
      "kanban": {
        id: "kanban",
        title: "KANBAN",
        subtitle: "Visualize workflow and maximize efficiency",
        description: "Visualize your project workflow with intuitive cards on a drag-and-drop board to track progress in real-time.",
        color: "#bbdefb",
        bgColor: "#2196f3",
        icon: "📊"
      },
      "scrum": {
        id: "scrum",
        title: "SCRUM",
        subtitle: "Iterative development with time-boxed sprints",
        description: "Organize work into fixed-length sprints with planning, daily standups, and retrospectives to continuously improve.",
        color: "#b2dfdb",
        bgColor: "#009688",
        icon: "🔄"
      },
      "business": {
        id: "business",
        title: "BUSINESS",
        subtitle: "Streamlined project management for business teams",
        description: "Perfect for marketing, finance, and operations teams with customizable workflows, reports, and stakeholder views.",
        color: "#c8e6c9",
        bgColor: "#4caf50",
        icon: "💼"
      }
    };
    
    if (project.type === "Software Development") {
      return templateMap.scrum;
    } else if (project.type === "Business" || project.type === "Marketing") {
      return templateMap.business;
    } else {
      return templateMap.kanban;
    }
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    
    try {
      const cleanedDateString = dateString.split('T')[0].split(' ')[0];
      const date = new Date(cleanedDateString);
      
      if (isNaN(date.getTime())) {
        //console.error("Invalid date:", dateString);
        return "";
      }
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch (error) {
      //console.error("Error formatting date:", error, dateString);
      return "";
    }
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
  };

  const handleViewTemplateDetails = () => {
    setCurrentPage("templateDetails");
  };

  const handleNavigateToProjectTypeSelection = () => {
    setCurrentPage("projectTypeSelection");
  };

  const handleNavigateToProjectForm = (type) => {
    setProjectType(type);
    setCurrentPage("projectForm");
  };

  const handleBackToTemplates = () => {
    setCurrentPage("templateSelection");
  };

  const handleBackToTemplateDetails = () => {
    setCurrentPage("templateDetails");
  };

  const handleBackToProjectTypeSelection = () => {
    setCurrentPage("projectTypeSelection");
  };

  const handleCreateProject = async (formData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const employee_id = await getEmployeeid();
      const baseUrl = `${API_URL}/api/projects`;
      const url = isEditMode 
        ? `${baseUrl}/${projectId}` 
        : `${baseUrl}?employee_id=${employee_id}`;

      if (isEditMode) {
        await axios.put(url, formData, { headers: { 'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`
         } });
        alert("Project updated successfully!");
      } else {
        await axios.post(url, formData, { headers: { 'Content-Type': 'multipart/form-data',   Authorization: `Bearer ${token}` } });

        // If created from an NPI proposal, sync NPI intake to Converted
        if (npiSource?.intake_id) {
          try {
            await axios.put(`${API_URL}/api/npi/${npiSource.intake_id}/status`, {
              status: "Converted",
              status_change_reason: "Project successfully created via Create Project wizard"
            }, { headers: { Authorization: `Bearer ${token}` } });
          } catch (npiErr) {
            //console.error("Could not sync NPI conversion status:", npiErr);
          }
        }

        alert("Project created successfully!");
      }

      localStorage.removeItem('editProject');
      navigate('/pro');
  
    } catch (error) {
      //console.error("Error saving project:", error);
      if (error.response?.data?.message) {
        alert(`Failed to save project: ${error.response.data.message}`);
      } else {
        alert("Failed to save project. Please try again.");
      }
      throw error;
    }
  };
  
  const getActiveStep = () => {
    switch (currentPage) {
      case "templateSelection":
        return 0;
      case "templateDetails":
        return 1;
      case "projectTypeSelection":
        return 2;
      case "projectForm":
        return 3;
      default:
        return 0;
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Typography variant="h4" gutterBottom  fontWeight="bold">
        {isEditMode ? "Edit Project" : "Create Project"}
      </Typography>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {!isEditMode && (
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={getActiveStep()} alternativeLabel>
              <Step>
                <StepLabel>Select Template</StepLabel>
              </Step>
              <Step>
                <StepLabel>Template Details</StepLabel>
              </Step>
              <Step>
                <StepLabel>Project Type</StepLabel>
              </Step>
              <Step>
                <StepLabel>Create Project</StepLabel>
              </Step>
            </Stepper>
          </Box>
        )}

        {currentPage === "templateSelection" && (
          <TemplateSelectionPage 
            onSelectTemplate={handleSelectTemplate}
            selectedTemplate={selectedTemplate}
            onNext={handleViewTemplateDetails}
          />
        )}
        
        {currentPage === "templateDetails" && (
          <TemplateDetailsPage 
            template={selectedTemplate}
            onNext={handleNavigateToProjectTypeSelection}
            onBack={handleBackToTemplates}
          />
        )}
        
        {currentPage === "projectTypeSelection" && (
          <ProjectTypeSelectionPage 
            template={selectedTemplate}
            onSelect={handleNavigateToProjectForm}
            onBack={handleBackToTemplateDetails}
          />
        )}
        
        {currentPage === "projectForm" && (
          <ProjectFormPage 
            initialData={projectData}
            selectedTemplate={selectedTemplate}
            projectType={projectType}
            onSubmit={handleCreateProject}
            onBack={handleBackToProjectTypeSelection}
            isEditMode={isEditMode}
            npiSource={npiSource}
            projectId={projectId}
          />
        )}
      </Container>
    </Box>
  );
}

// Template Selection Page Component
function TemplateSelectionPage({ onSelectTemplate, selectedTemplate, onNext }) {
  const templates = [
    {
      id: "kanban",
      title: "KANBAN",
      subtitle: "Visualize workflow and maximize efficiency",
      description: "Visualize your project workflow with intuitive cards on a drag-and-drop board to track progress in real-time.",
      color: "#bbdefb",
      bgColor: "#2196f3",
      icon: "📊"
    },
    {
      id: "scrum",
      title: "SCRUM",
      subtitle: "Iterative development with time-boxed sprints",
      description: "Organize work into fixed-length sprints with planning, daily standups, and retrospectives to continuously improve.",
      color: "#b2dfdb",
      bgColor: "#009688",
      icon: "🔄"
    },
    {
      id: "business",
      title: "BUSINESS",
      subtitle: "Streamlined project management for business teams",
      description: "Perfect for marketing, finance, and operations teams with customizable workflows, reports, and stakeholder views.",
      color: "#c8e6c9",
      bgColor: "#4caf50",
      icon: "💼"
    }
  ];

  return (
    <Paper elevation={2} sx={{ p: 4 }}>
      <Typography variant="h4" align="center" fontWeight="bold" sx={{ mb: 4 }}>
        Select a template for your project
      </Typography>
      
      <Grid container spacing={3}>
        {templates.map((template) => (
          <Grid item xs={12} md={4} key={template.id}>
            <Card 
              sx={{ 
                height: "100%", 
                cursor: "pointer",
                transition: "all 0.3s",
                boxShadow: selectedTemplate?.id === template.id ? 4 : 1,
                border: selectedTemplate?.id === template.id ? 2 : 0,
                borderColor: selectedTemplate?.id === template.id ? "primary.main" : "transparent",
                "&:hover": {
                  boxShadow: 4
                }
              }}
              onClick={() => onSelectTemplate(template)}
            >
              <CardMedia
                component="div"
                sx={{
                  bgcolor: template.bgColor,
                  height: 150,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "3rem"
                }}
              >
                {template.icon}
              </CardMedia>
              <CardContent>
                <Typography variant="h6" component="h3" fontWeight="bold">
                  {template.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {template.subtitle}
                </Typography>
                <Typography variant="body2">
                  {template.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      
      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onNext}
          disabled={!selectedTemplate}
          endIcon={<KeyboardArrowRight />}
        >
          Next
        </Button>
      </Box>
    </Paper>
  );
}

function TemplateDetailsPage({ template, onNext, onBack }) {
  if (!template) return null;

  const getTemplateBenefits = () => {
    if (template.id === "kanban") {
      return [
        "Visualize tasks and workflow stages at a glance",
        "Identify and eliminate bottlenecks quickly",
        "Optimize resource allocation across projects",
        "Improve delivery predictability with flow metrics"
      ];
    } else if (template.id === "scrum") {
      return [
        "Deliver value in short, focused iterations",
        "Adapt to changing requirements efficiently",
        "Increase team collaboration and ownership",
        "Facilitate continuous improvement through retrospectives"
      ];
    } else {
      return [
        "Streamline cross-functional team coordination",
        "Enhance visibility into project financials",
        "Simplify resource planning and allocation",
        "Create professional dashboards for stakeholders"
      ];
    }
  };

  const getBreadcrumbs = () => (
    <Breadcrumbs sx={{ mb: 2 }}>
      <Link
        component="button"
        variant="body2"
        color="inherit"
        onClick={onBack}
        sx={{ textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
      >
        Project templates
      </Link>
      <Typography color="text.primary">{template.title}</Typography>
    </Breadcrumbs>
  );

  return (
    <Box>
      {getBreadcrumbs()}
      
      <Paper elevation={3} sx={{ p: 0, mb: 4, overflow: "hidden" }}>
        <Box sx={{ p: 4, bgcolor: template.bgColor, color: "white", height: "100px" }}>
          <Typography variant="h5" gutterBottom>{template.title}</Typography>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {template.subtitle}
          </Typography>
        </Box>
        
        <Box sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ mb: 4 }}>
            {template.id === "kanban" ? (
              <>
                Kanban is a visual workflow management method that helps teams optimize productivity and efficiency. Originating from Toyota's manufacturing principles, modern Kanban helps teams visualize their work, limit work-in-progress, and identify bottlenecks to continuously improve flow and deliver value faster. It adapts to your existing workflow while providing powerful metrics to drive improvement.
              </>
            ) : template.id === "scrum" ? (
              <>
                Scrum is a lightweight agile framework that empowers teams to tackle complex problems through iterative development. With fixed-length sprints, daily synchronization meetings, and defined roles, Scrum creates a rhythm of planning, execution, and reflection. Teams build a product increment each sprint, gathering feedback and adjusting priorities to maximize value delivery.
              </>
            ) : (
              <>
                The Business template provides a flexible environment for managing non-software projects with business-oriented workflows and views. Designed for marketing campaigns, finance projects, operations initiatives, and cross-functional collaboration, this template comes with customizable reporting tools, resource management, and specialized views for different stakeholder needs.
              </>
            )}
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Key Features</Typography>
              <List>
                {getTemplateBenefits().map((benefit, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <Check color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={benefit} />
                  </ListItem>
                ))}
              </List>
            </Grid>
          </Grid>
          
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 6 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={onBack}
              startIcon={<ArrowBack />}
            >
              Back
            </Button>
            
            <Button
              variant="contained"
              color="primary"
              onClick={onNext}
              endIcon={<KeyboardArrowRight />}
            >
              Use template
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

function ProjectTypeSelectionPage({ template, onSelect, onBack }) {
  if (!template) return null;

  return (
    <Box>
      <Box 
        sx={{ 
          display: "flex", 
          alignItems: "center", 
          color: "primary.main", 
          cursor: "pointer", 
          mb: 2,
          "&:hover": { textDecoration: "underline" } 
        }}
        onClick={onBack}
      >
        <ArrowBack fontSize="small" sx={{ mr: 1 }} />
        <Typography>Back to template details</Typography>
      </Box>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box 
            sx={{ 
              width: 48, 
              height: 48, 
              borderRadius: 1, 
              color: "white", 
              display: "flex", 
              alignItems: "center", 
              justifyContent: "center", 
              fontSize: "1.5rem", 
              mr: 2 
            }}
          >
            {template.icon}
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight="bold">{template.title}</Typography>
            <Typography variant="body2" color="text.secondary">{template.description}</Typography>
          </Box>
        </Box>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" fontWeight="bold">Choose a project type</Typography>
        </Box>
        
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ borderTop: 4, borderColor: "primary.main", pt: 2 }}>
              <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{ mb: 1 }}>
                Team-managed
              </Typography>
              <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                Autonomous and self-contained workspaces.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Perfect for teams that want complete control over their processes, tools, and workflows. Team members can customize their workspace as needed and adapt quickly to changing requirements without dependencies on other teams.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => onSelect("team")}
              >
                Select a team-managed project
              </Button>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ borderTop: 4, borderColor: "primary.main", pt: 2 }}>
              <Typography variant="h6" color="primary.dark" fontWeight="bold" sx={{ mb: 1 }}>
                Company-managed
              </Typography>
              <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
                Standardized environments with central governance.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Ideal for enterprise coordination where consistency and compliance are essential. Centralized administration ensures alignment with organizational standards while enabling seamless collaboration across multiple teams and departments.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => onSelect("company")}
              >
                Select a company-managed project
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

function DocumentUploadAI({ onFieldsExtracted, isProcessing, setIsProcessing }) {
  const [uploadError, setUploadError] = useState("");
  const [uploadSeverity, setUploadSeverity] = useState("error");

  const handleDocumentUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];

    if (!allowedTypes.includes(file.type)) {
      setUploadSeverity("warning");
      setUploadError("Only PDF, DOC, DOCX, and TXT files are supported for AI analysis");
      event.target.value = '';
      return;
    }

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadSeverity("warning");
      setUploadError("File size must be less than 10MB");
      event.target.value = '';
      return;
    }

    setIsProcessing(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append('uploadDocument', file);

      const response = await axios.post(
        `${API_URL}/api/projects/process-document`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`
           }
        }
      );

      if (response.data.status === "success") {
        // Separate extracted vs suggested fields for UI display
        const extracted = {
          name: response.data.data.name,
          projectType: response.data.data.projectType,
          description: response.data.data.description,
          // Do NOT set key - backend will generate
          key: null, // Explicitly null
        };
        const suggestions = {
          startDate: response.data.data.startDate,
          endDate: response.data.data.endDate,
          budget: response.data.data.budget,
          // Flag if these were suggested (AI always suggests if missing)
          isSuggested: {
            startDate: true, // Assume suggested unless explicit
            endDate: true,
            budget: true
          }
        };
        
        onFieldsExtracted({ extracted, suggestions, file });
        setUploadSeverity("success");
        setUploadError("✨ Document analyzed! Fields auto-filled. Review suggested timeline & budget below.");
      }
    } catch (error) {
      //console.error("Error processing document:", error);
      const errorData = error.response?.data;

      if (errorData?.code === "AI_PLAN_UPGRADE_REQUIRED") {
        setUploadSeverity("warning");
        setUploadError(errorData.message || "AI features are not included in your current plan. Please upgrade your plan to  unlock AI.");
      } else if (errorData?.code === "AI_TOKENS_EXHAUSTED") {
        setUploadSeverity("warning");
        setUploadError(`⚠️ ${errorData.message || "You have used all your AI tokens for this billing cycle."}`);
      } else {
        setUploadSeverity("error");
        setUploadError(
          errorData?.message || 
          "Failed to analyze document. Please try again or fill fields manually."
        );
      }
    } finally {
      setIsProcessing(false);
      event.target.value = '';
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Paper 
        elevation={1}
        sx={{ 
          p: 3,
          border: "2px solid",
          borderColor: "primary.main",
          bgcolor: "#f8f9fa"
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <AutoAwesome sx={{ fontSize: 28, color: "primary.main" }} />
          <Box>
            <Typography variant="h6" fontWeight="600" color="text.primary">
              AI Document Auto-Fill
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload your project document for automatic field extraction
            </Typography>
          </Box>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          AI will extract: Project name, type, description, timeline, and budget from your document.
        </Alert>

        <input
          accept=".pdf,.doc,.docx,.txt"
          style={{ display: 'none' }}
          id="ai-document-upload"
          type="file"
          onChange={handleDocumentUpload}
          disabled={isProcessing}
        />
        <label htmlFor="ai-document-upload">
          <Button
            component="span"
            variant="contained"
            fullWidth
            startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
            disabled={isProcessing}
            sx={{ py: 1.5 }}
          >
            {isProcessing ? "AI Analyzing Document..." : "Upload Document for AI Analysis"}
          </Button>
        </label>

        {uploadError && (
          <Alert severity={uploadSeverity} sx={{ mt: 2 }}>
            {uploadError}
          </Alert>
        )}

        <Box sx={{ mt: 2, p: 1.5, bgcolor: "white", borderRadius: 1, border: "1px solid #e0e0e0" }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            • Supported formats: DOC
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            • Maximum file size: 10MB
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            • Note: Project Key is auto-generated and cannot be extracted from documents
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

// Project Form Page Component with AI Enhancements
function ProjectFormPage({ initialData, selectedTemplate, projectType, onSubmit, onBack, isEditMode = false, npiSource = null, projectId = null }) {
  const [projectData, setProjectData] = useState({
    ...initialData,
    basicInfo: {
      ...initialData.basicInfo,
      projectType: initialData.basicInfo.projectType || (selectedTemplate?.id === "business" ? "Business" : "Software")
    }
  });

  const [hoursMetrics, setHoursMetrics] = useState(initialData?.hoursMetrics || null);
  const [originalBudget, setOriginalBudget] = useState(initialData?.timeline?.originalBudget || initialData?.timeline?.budget || "");

  // Sync initialData changes (e.g., from NPI proposal redirection or loaded project)
  useEffect(() => {
    if (initialData?.basicInfo) {
      setProjectData(prev => ({
        ...prev,
        basicInfo: {
          ...prev.basicInfo,
          ...initialData.basicInfo,
          projectType: initialData.basicInfo.projectType || prev.basicInfo.projectType || "Software"
        },
        timeline: {
          ...prev.timeline,
          ...initialData.timeline
        }
      }));
    }
    if (initialData?.timeline?.originalBudget) {
      setOriginalBudget(initialData.timeline.originalBudget);
    } else if (initialData?.timeline?.budget && !originalBudget) {
      setOriginalBudget(initialData.timeline.budget);
    }
    if (initialData?.hoursMetrics) {
      setHoursMetrics(initialData.hoursMetrics);
    }
  }, [initialData]);

  useEffect(() => {
    if (isEditMode && projectId && !hoursMetrics) {
      const fetchMetrics = async () => {
        try {
          const token = localStorage.getItem('token');
          if (!token) return;
          const res = await axios.get(`${API_URL}/api/projects/${projectId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (res.data?.data?.hoursMetrics) {
            setHoursMetrics(res.data.data.hoursMetrics);
          }
          if (res.data?.data?.original_budget) {
            setOriginalBudget(String(res.data.data.original_budget));
          } else if (res.data?.data?.budget && !originalBudget) {
            setOriginalBudget(String(res.data.data.budget));
          }
          const loadedReason = res.data?.data?.budget_revision_reason || res.data?.data?.reason || "";
          if (loadedReason && !projectData.timeline.reason) {
            setProjectData(prev => ({
              ...prev,
              timeline: {
                ...prev.timeline,
                reason: loadedReason,
                notes: res.data?.data?.notes || ""
              }
            }));
          }
        } catch (err) {
          //console.error("Failed to fetch project hours metrics:", err);
        }
      };
      fetchMetrics();
    }
  }, [isEditMode, projectId]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [newProjectType, setNewProjectType] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadError, setUploadError] = useState("");
  const [isDocProcessing, setIsDocProcessing] = useState(false);
  const [aiExtracted, setAiExtracted] = useState(null); // For extracted fields
  const [aiSuggestionsFromDoc, setAiSuggestionsFromDoc] = useState(null); // For suggested dates/budget from doc
  const [showDocSuggestions, setShowDocSuggestions] = useState(false);
  const [costCenters, setCostCenters] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [currentEmployeeId, setCurrentEmployeeId] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;

        let company_id = null;
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          company_id = payload.company_id;
        } catch (e) {}

        const promises = [
          axios.get(`${API_URL}/api/cost-centers`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
          axios.get(`${API_URL}/api/viewpersonal?limit=1000`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })),
          axios.get(`${API_URL}/api/user/a/b/c`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} }))
        ];

        if (company_id) {
          promises.push(axios.get(`${API_URL}/api/user/current?company_id=${company_id}`, { withCredentials: true }).catch(() => ({ data: {} })));
          promises.push(axios.get(`${API_URL}/api/cmp/${company_id}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: {} })));
        }

        const resolvedPromises = await Promise.all(promises);
        const ccRes = resolvedPromises[0];
        const empRes = resolvedPromises[1];
        const userRes = resolvedPromises[2];
        let currUserRes, cmpRes;

        if (company_id) {
          currUserRes = resolvedPromises[3];
          cmpRes = resolvedPromises[4];
        }

        if (ccRes?.data?.success) {
          setCostCenters(ccRes.data.data || []);
        }
        if (empRes?.data?.data) {
          setAllEmployees(empRes.data.data || []);
        }
        if (userRes?.data?.data) {
          const map = {};
          userRes.data.data.forEach(u => {
            if (u.email) map[u.email.toLowerCase()] = u.role;
          });
          setUsersMap(map);
        }
        if (currUserRes?.data?.employee_id) {
          setCurrentEmployeeId(currUserRes.data.employee_id);
        }
        if (cmpRes?.data?.data) {
          setCompanyConfig(cmpRes.data.data);
        }
      } catch (error) {
        //console.error("Error fetching project metadata:", error);
      }
    };
    fetchMetadata();
  }, []);

  const userRole = (localStorage.getItem("role") || "").toLowerCase();
  const isProjectManager = userRole === "project-manager" || userRole === "manager";
  const isProductOwner = userRole === "product-owner";
  const isProductManager = userRole === "product-manager";

  useEffect(() => {
    if (isEditMode) return;
    if (!currentEmployeeId) return;

    setProjectData(prev => {
      const basic = { ...prev.basicInfo };
      let updated = false;

      if (isProjectManager && (!basic.projectManagerId || basic.projectManagerId === "")) {
        basic.projectManagerId = currentEmployeeId;
        updated = true;
      }
      if (isProductOwner && (!basic.productOwnerId || basic.productOwnerId === "")) {
        basic.productOwnerId = currentEmployeeId;
        updated = true;
      }
      if (isProductManager && (!basic.productManagerId || basic.productManagerId === "")) {
        basic.productManagerId = currentEmployeeId;
        updated = true;
      }

      return updated ? { ...prev, basicInfo: basic } : prev;
    });
  }, [currentEmployeeId, isProjectManager, isProductOwner, isProductManager, isEditMode]);

  const currentEmpObj = allEmployees.find(e => String(e.employee_id || e.id) === String(currentEmployeeId));
  const currentEmpName = currentEmpObj ? `${currentEmpObj.firstName || ''} ${currentEmpObj.lastName || ''}`.trim() : "Logged-in User";

  const getEmployeeRole = (emp) => {
    if (!emp) return "";
    const emailKey = (emp.companyemail || emp.personalemail || "").toLowerCase();
    return emp.role || emp.designation || emp.job_title || emp.position || usersMap[emailKey] || "";
  };

  const getRoleOptions = (roleKeywords, selectedId) => {
    if (!allEmployees || allEmployees.length === 0) {
      if (selectedId) {
        return [{ employee_id: String(selectedId), id: String(selectedId), firstName: "Assigned", lastName: `(${selectedId})` }];
      }
      return [];
    }

    const matching = [];
    const others = [];

    allEmployees.forEach(emp => {
      const r = getEmployeeRole(emp).toLowerCase();
      const isMatch = roleKeywords.some(kw => r.includes(kw.toLowerCase()));
      if (isMatch) {
        matching.push(emp);
      } else {
        others.push(emp);
      }
    });

    const combined = [...matching, ...others];

    if (selectedId && !combined.some(e => String(e.employee_id || e.id) === String(selectedId))) {
      combined.unshift({
        employee_id: String(selectedId),
        id: String(selectedId),
        firstName: "Assigned",
        lastName: `(${selectedId})`
      });
    }

    return combined;
  };

  const projectManagersList = getRoleOptions(["project-manager", "project manager", "manager", "lead"], projectData.basicInfo.projectManagerId);
  const productManagersList = getRoleOptions(["product-manager", "product manager", "product", "manager"], projectData.basicInfo.productManagerId);
  const productOwnersList = getRoleOptions(["product-owner", "product owner", "owner", "product"], projectData.basicInfo.productOwnerId);

  const [projectTypes, setProjectTypes] = useState([
    { value: "Software", label: "Software Development" },
    { value: "Hardware", label: "Hardware" },
    { value: "Service", label: "Service Management" },
    { value: "Work", label: "Work Development" },
    { value: "Product", label: "Product Management" },
    { value: "Marketing", label: "Marketing" },
    { value: "HR", label: "Human Resources" },
    { value: "Finance", label: "Finance" },
    { value: "Design", label: "Design" },
    { value: "Personal", label: "Personal" },
    { value: "Operations", label: "Operations" },
    { value: "Legal", label: "Legal" },
    { value: "Sales", label: "Sales" },
    { value: "Analytics", label: "Analytics" },
    { value: "IT", label: "IT" },
    { value: "Facilities", label: "Facilities" },
    { value: "Nonprofit", label: "Nonprofit" },
  ]);

  // Handle AI-extracted fields from document
  const handleDocumentFieldsExtracted = ({ extracted, suggestions, file }) => {


    // Apply extracted fields (no key)
    setProjectData(prev => ({
      ...prev,
      basicInfo: {
        ...prev.basicInfo,
        name: extracted.name || prev.basicInfo.name,
        projectType: extracted.projectType || prev.basicInfo.projectType,
        description: extracted.description || prev.basicInfo.description,
        // Key remains unchanged (backend generates)
        key: prev.basicInfo.key
      }
    }));

    // Set suggestions for display (similar to AI suggestions)
    setAiSuggestionsFromDoc(suggestions);
    setShowDocSuggestions(true);

    setAiExtracted(extracted);

    // Automatically register the uploaded document as the project's PRD document
    if (file) {
      setUploadedFiles([{
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: file.size,
        type: file.type
      }]);
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'image/jpeg',
      'image/png',
      'image/gif'
    ];
    
    const maxSize = 10 * 1024 * 1024;
    
    const validFiles = [];
    let hasError = false;
    
    files.forEach(file => {
      if (!allowedTypes.includes(file.type)) {
        setUploadError(`File "${file.name}" is not a supported format.`);
        hasError = true;
        return;
      }
      
      if (file.size > maxSize) {
        setUploadError(`File "${file.name}" is too large. Maximum size is 10MB.`);
        hasError = true;
        return;
      }
      
      validFiles.push({
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: file.size,
        type: file.type
      });
    });
    
    if (!hasError) {
      setUploadedFiles(prev => [...prev, ...validFiles]);
      setUploadError("");
    }
    
    event.target.value = '';
  };
  
  const handleRemoveFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };
  
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  const getFileIcon = (fileType) => {
    if (fileType.includes('pdf')) return <PictureAsPdf color="error" />;
    if (fileType.includes('image')) return <InsertDriveFile color="primary" />;
    if (fileType.includes('word') || fileType.includes('document')) return <DocIcon color="info" />;
    return <AttachFile color="action" />;
  };
  
  const handleBasicInfoChange = (e) => {
    const { name, value } = e.target;
    setProjectData({
      ...projectData,
      basicInfo: {
        ...projectData.basicInfo,
        [name]: value
      }
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  };
  
  const handleTimelineChange = (e) => {
    const { name, value } = e.target;
    setProjectData({
      ...projectData,
      timeline: {
        ...projectData.timeline,
        [name]: value
      }
    });
    
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ""
      });
    }
  };
  
  const generateProjectKey = () => {
    if (projectData.basicInfo.name && !isEditMode) {
      const words = projectData.basicInfo.name.split(" ");
      let key = "";
      
      for (let i = 0; i < Math.min(words.length, 3); i++) {
        if (words[i]) {
          key += words[i][0].toUpperCase();
        }
      }
      
      if (key.length < 3) {
        key += "PRJ";
      }
      
      setProjectData({
        ...projectData,
        basicInfo: {
          ...projectData.basicInfo,
          key: key
        }
      });
    }
  };

  const handleAddProjectType = async () => {
    if (!newProjectType.trim()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
    
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      const response = await axios.post(`${API_URL}/api/projects/type`, {
        value: newProjectType.trim(),
        label: newProjectType.trim(),
        
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        
      });
      
      if (response.data.status === "success") {
        const newType = {
          value: response.data.data.value,
          label: response.data.data.label
        };
        
        const updatedTypes = [...projectTypes, newType];
        setProjectTypes(updatedTypes);
        
        setProjectData({
          ...projectData,
          basicInfo: {
            ...projectData.basicInfo,
            projectType: newType.value
          }
        });
        
        handleCloseDialog();
      }
    } catch (error) {
      //console.error("Error creating project type:", error);
      
      if (error.response && error.response.data) {
        setApiError(error.response.data.message || "Failed to create project type");
      } else {
        setApiError("Failed to create project type. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!projectData.basicInfo.name.trim()) {
      newErrors.name = "Project name is required";
    }
    
    if (!projectData.basicInfo.key.trim()) {
      newErrors.key = "Project key is required";
    } else if (!/^[A-Z][A-Z0-9]{2,9}$/.test(projectData.basicInfo.key)) {
      newErrors.key = "Project key must be 3-10 uppercase letters/numbers and start with a letter";
    }
    
    if (!projectData.basicInfo.projectType) {
      newErrors.projectType = "Project type is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      validateForm();
    }
  }, [projectData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Ensure key is set (generate if missing)
    if (!projectData.basicInfo.key && projectData.basicInfo.name) {
      const words = projectData.basicInfo.name.split(" ");
      let key = "";
      for (let i = 0; i < Math.min(words.length, 3); i++) {
        if (words[i]) {
          key += words[i][0].toUpperCase();
        }
      }
      if (key.length < 3) {
        key += "PRJ";
      }
      setProjectData(prev => ({
        ...prev,
        basicInfo: { ...prev.basicInfo, key }
      }));
    }
    
    setLoading(true);
    setApiError(null);
    
    try {
      const submitData = new FormData();
      
      submitData.append('basicInfo', JSON.stringify(projectData.basicInfo));
      submitData.append('timeline', JSON.stringify(projectData.timeline));
      submitData.append('selectedTemplate', JSON.stringify(selectedTemplate));
      submitData.append('projectType', projectType);
      
      if (uploadedFiles.length > 0) {
        uploadedFiles.forEach((file) => {
          submitData.append('uploadDocument', file.file);
        });
      }
      
      await onSubmit(submitData);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      //console.error("Error saving project:", error);
      
      if (error.response) {
        setApiError(error.response.data.message || "Server error occurred");
      } else if (error.request) {
        setApiError("No response received from server. Please check your connection.");
      } else {
        setApiError(error.message || "An unexpected error occurred");
      }
    }
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setNewProjectType("");
  };

  useEffect(() => {
    if (!isEditMode && projectData.basicInfo.name && !projectData.basicInfo.key) {
      generateProjectKey();
    }
  }, [projectData.basicInfo.name, isEditMode]);

  return (
    <Paper sx={{ p: 4 }}>
      <Typography variant="h4" align="center" fontWeight="bold" sx={{ mb: 4 }}>
        {isEditMode ? `Edit ${projectType === "team" ? "Team-Managed" : "Company-Managed"} Project` : `Create ${projectType === "team" ? "Team-Managed" : "Company-Managed"} Project`}
      </Typography>
      
      {apiError && (
        <Box sx={{ mb: 3, p: 2, bgcolor: "#ffebee", borderRadius: 1 }}>
          <Typography color="error">{apiError}</Typography>
        </Box>
      )}
      
      {!isEditMode && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Box 
            sx={{ 
              display: "inline-flex", 
              alignItems: "center", 
              color: "primary.main", 
              cursor: "pointer", 
              "&:hover": { textDecoration: "underline" } 
            }}
            onClick={onBack}
          >
            <ArrowBack fontSize="small" sx={{ mr: 1 }} />
            <Typography>Back to project type</Typography>
          </Box>
        </Box>
      )}

      {/* NPI Proposal Conversion Banner */}
      {npiSource && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          Converting from NPI Proposal: <strong>{npiSource.title}</strong>. Details, dates, and budget have been pre-filled from the approved proposal. Leadership roles are auto-detected based on your login.
        </Alert>
      )}

      {/* Document Upload AI Section */}
      {!isEditMode && (
        <DocumentUploadAI
          onFieldsExtracted={handleDocumentFieldsExtracted}
          isProcessing={isDocProcessing}
          setIsProcessing={setIsDocProcessing}
        />
      )}
      
      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            <Description sx={{ mr: 1, verticalAlign: "middle" }} />
            Basic Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Project Name"
                name="name"
                fullWidth
                required
                value={projectData.basicInfo.name}
                onChange={handleBasicInfoChange}
                onBlur={generateProjectKey}
                error={!!errors.name}
                helperText={errors.name || "Give your project a descriptive name"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Project Key"
                name="key"
                fullWidth
                required
                value={projectData.basicInfo.key}
                onChange={handleBasicInfoChange}
                disabled={isEditMode}
                error={!!errors.key}
                helperText={errors.key || "3-10 character unique identifier (auto-generated)"}
                InputProps={{
                  sx: isEditMode ? { bgcolor: "#f5f5f5" } : {}
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.projectType} disabled={loading}>
                <InputLabel id="project-type-label">Project type</InputLabel>
                <Select
                  labelId="project-type-label"
                  id="project-type"
                  name="projectType"
                  value={projectData.basicInfo.projectType}
                  onChange={handleBasicInfoChange}
                  label="Project type"
                >
                  {projectTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.projectType && (
                  <FormHelperText>{errors.projectType}</FormHelperText>
                )}
              </FormControl>
              
              <Grid item>
                <Button
                  startIcon={<Add />}
                  onClick={handleOpenDialog}
                  sx={{ mt: 1 }}
                  disabled={loading}
                >
                  Add Custom project type
                </Button>
              </Grid>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={loading}>
                <InputLabel id="cost-center-label">Cost Center (Optional)</InputLabel>
                <Select
                  labelId="cost-center-label"
                  id="cost-center"
                  name="costCenterId"
                  value={projectData.basicInfo.costCenterId || ""}
                  onChange={handleBasicInfoChange}
                  label="Cost Center (Optional)"
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {costCenters.map((cc) => (
                    <MenuItem key={cc.id} value={cc.id}>
                      {cc.name} ({cc.code})
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Optionally link a cost center to automatically assign to project team members</FormHelperText>
              </FormControl>
            </Grid>
            {/* Project Manager Field */}
            <Grid item xs={12} md={4}>
              {isProjectManager && !isEditMode ? (
                <TextField
                  fullWidth
                  label="Project Manager"
                  value={`${currentEmpName} (Auto-assigned)`}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  helperText="Auto-assigned to you (Logged-in Project Manager)"
                />
              ) : (
                <FormControl fullWidth disabled={loading}>
                  <InputLabel id="project-manager-label">Project Manager (Optional)</InputLabel>
                  <Select
                    labelId="project-manager-label"
                    id="project-manager"
                    name="projectManagerId"
                    value={projectData.basicInfo.projectManagerId || ""}
                    onChange={handleBasicInfoChange}
                    label="Project Manager (Optional)"
                  >
                    <MenuItem value="">
                      <em>None (Unassigned)</em>
                    </MenuItem>
                    {projectManagersList.map((emp) => (
                      <MenuItem key={emp.employee_id || emp.id} value={emp.employee_id || emp.id}>
                        {emp.firstName} {emp.lastName} {getEmployeeRole(emp) ? `(${getEmployeeRole(emp)})` : (emp.employee_id ? `(${emp.employee_id})` : '')}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select designated Project Manager</FormHelperText>
                </FormControl>
              )}
            </Grid>

            {/* Product Manager Field */}
            <Grid item xs={12} md={4}>
              {isProductManager && !isEditMode ? (
                <TextField
                  fullWidth
                  label="Product Manager"
                  value={`${currentEmpName} (Auto-assigned)`}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  helperText="Auto-assigned to you (Logged-in Product Manager)"
                />
              ) : (
                <FormControl fullWidth disabled={loading}>
                  <InputLabel id="product-manager-label">Product Manager (Optional)</InputLabel>
                  <Select
                    labelId="product-manager-label"
                    id="product-manager"
                    name="productManagerId"
                    value={projectData.basicInfo.productManagerId || ""}
                    onChange={handleBasicInfoChange}
                    label="Product Manager (Optional)"
                  >
                    <MenuItem value="">
                      <em>None (Unassigned)</em>
                    </MenuItem>
                    {productManagersList.map((emp) => (
                      <MenuItem key={emp.employee_id || emp.id} value={emp.employee_id || emp.id}>
                        {emp.firstName} {emp.lastName} {getEmployeeRole(emp) ? `(${getEmployeeRole(emp)})` : (emp.employee_id ? `(${emp.employee_id})` : '')}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select designated Product Manager for feature roadmap</FormHelperText>
                </FormControl>
              )}
            </Grid>

            {/* Product Owner Field */}
            <Grid item xs={12} md={4}>
              {isProductOwner && !isEditMode ? (
                <TextField
                  fullWidth
                  label="Product Owner"
                  value={`${currentEmpName} (Auto-assigned)`}
                  InputProps={{ readOnly: true }}
                  variant="filled"
                  helperText="Auto-assigned to you (Logged-in Product Owner)"
                />
              ) : (
                <FormControl fullWidth disabled={loading}>
                  <InputLabel id="product-owner-label">Product Owner (Optional)</InputLabel>
                  <Select
                    labelId="product-owner-label"
                    id="product-owner"
                    name="productOwnerId"
                    value={projectData.basicInfo.productOwnerId || ""}
                    onChange={handleBasicInfoChange}
                    label="Product Owner (Optional)"
                  >
                    <MenuItem value="">
                      <em>None (Unassigned)</em>
                    </MenuItem>
                    {productOwnersList.map((emp) => (
                      <MenuItem key={emp.employee_id || emp.id} value={emp.employee_id || emp.id}>
                        {emp.firstName} {emp.lastName} {getEmployeeRole(emp) ? `(${getEmployeeRole(emp)})` : (emp.employee_id ? `(${emp.employee_id})` : '')}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Select designated Product Owner for user story backlog</FormHelperText>
                </FormControl>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Project Management"
                value={projectType === "team" ? "Team-managed project" : "Company-managed project"}
                InputProps={{
                  readOnly: true,
                }}
                variant="filled"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Project Description"
                name="description"
                fullWidth
                required
                multiline
                rows={4}
                value={projectData.basicInfo.description}
                onChange={handleBasicInfoChange}
                error={!!errors.description}
                helperText={errors.description || "Describe the purpose and goals of this project"}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Document AI Suggestions */}
        {showDocSuggestions && aiSuggestionsFromDoc && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Check color="success" />
                AI Suggestions from Document
              </Typography>
              <Button 
                variant="contained"
                color="success"
                size="small"
                onClick={() => {
                  setProjectData(prev => {
                    let updated = { ...prev };
                    const timeline = { ...updated.timeline };
                    if (aiSuggestionsFromDoc.startDate) timeline.startDate = aiSuggestionsFromDoc.startDate;
                    if (aiSuggestionsFromDoc.endDate) timeline.endDate = aiSuggestionsFromDoc.endDate;
                    if (aiSuggestionsFromDoc.budget) timeline.budget = String(aiSuggestionsFromDoc.budget);
                    updated.timeline = timeline;
                    return updated;
                  });
                  setShowDocSuggestions(false);
                }}
              >
                Apply Document Suggestions
              </Button>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2, bgcolor: "#e8f5e8", border: "1px solid", borderColor: "#4caf50" }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>📅 Start Date</Typography>
                  <Typography variant="body2">{aiSuggestionsFromDoc.startDate || 'Today'}</Typography>
                  {aiSuggestionsFromDoc.isSuggested?.startDate && (
                    <Typography variant="caption" color="text.secondary"> (AI Suggested)</Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2, bgcolor: "#fff3e0", border: "1px solid", borderColor: "#ff9800" }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>📅 End Date</Typography>
                  <Typography variant="body2">{aiSuggestionsFromDoc.endDate}</Typography>
                  {aiSuggestionsFromDoc.isSuggested?.endDate && (
                    <Typography variant="caption" color="text.secondary"> (AI Suggested)</Typography>
                  )}
                </Paper>
              </Grid>
              <Grid item xs={12} md={4}>
                <Paper elevation={2} sx={{ p: 2, bgcolor: "#e3f2fd", border: "1px solid", borderColor: "#2196f3" }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>💰 Budget</Typography>
                  <Typography variant="body2">{companyConfig?.currency_symbol || '₹'}{aiSuggestionsFromDoc.budget?.toLocaleString()}</Typography>
                  {aiSuggestionsFromDoc.isSuggested?.budget && (
                    <Typography variant="caption" color="text.secondary"> (AI Suggested)</Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
        
        <Divider sx={{ my: 4 }} />
        
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            <CalendarToday sx={{ mr: 1, verticalAlign: "middle" }} />
            {isEditMode ? "Timeline & Budget Governance" : "Timeline & Budget"}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Start Date"
                name="startDate"
                type="date"
                fullWidth
                required
                value={projectData.timeline.startDate}
                onChange={handleTimelineChange}
                error={!!errors.startDate}
                helperText={errors.startDate}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="End Date"
                name="endDate"
                type="date"
                fullWidth
                required
                value={projectData.timeline.endDate}
                onChange={handleTimelineChange}
                error={!!errors.endDate}
                helperText={errors.endDate}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={isEditMode ? "Project Budget (Current / Revised)" : "Budget"}
                name="budget"
                fullWidth
                required
                value={projectData.timeline.budget}
                onChange={handleTimelineChange}
                error={!!errors.budget}
                helperText={
                  errors.budget ||
                  (isEditMode
                    ? (originalBudget && String(originalBudget) !== String(projectData.timeline.budget)
                        ? `Original Budget: ${companyConfig?.currency_symbol || '₹'}${Number(originalBudget).toLocaleString()} (Budget Revised)`
                        : "Current project budget. Adjust if revising for actual hours.")
                    : "Total budget for this project")
                }
                InputProps={{
                  startAdornment: <InputAdornment position="start">{companyConfig?.currency_symbol || '₹'}</InputAdornment>,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Buffer Hours"
                name="bufferHours"
                type="number"
                fullWidth
                value={projectData.timeline.bufferHours || ""}
                onChange={handleTimelineChange}
                placeholder="e.g., 40"
                helperText="Contingency / buffer hours allocated for this project"
                InputProps={{
                  endAdornment: <InputAdornment position="end">hrs</InputAdornment>,
                }}
              />
            </Grid>

            {isEditMode && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="Budget Revision Reason / Notes"
                  name="reason"
                  fullWidth
                  value={projectData.timeline.reason || ""}
                  onChange={handleTimelineChange}
                  placeholder="e.g., Extra hours incurred due to client scope changes"
                  helperText="Reason/justification for revising the budget or timeline"
                />
              </Grid>
            )}

            {isEditMode && hoursMetrics && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: hoursMetrics.isHoursOverrun ? '#f59e0b' : '#cbd5e1',
                    background: hoursMetrics.isHoursOverrun 
                      ? 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)' 
                      : '#f8fafc'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TrendingUp sx={{ color: hoursMetrics.isHoursOverrun ? '#d97706' : '#2563eb' }} />
                      <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
                        Effort & Budget Variance Analysis
                      </Typography>
                    </Box>
                    <Chip
                      label={
                        hoursMetrics.isHoursOverrun
                          ? `Effort Overrun: +${hoursMetrics.hoursVariance} hrs (+${hoursMetrics.overrunPercentage}%)`
                          : `Effort Tracked: ${hoursMetrics.totalActualHours} / ${hoursMetrics.totalPlannedHours} hrs`
                      }
                      color={hoursMetrics.isHoursOverrun ? "warning" : "success"}
                      size="small"
                      sx={{ fontWeight: 'bold' }}
                    />
                  </Box>

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Planned Effort</Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {hoursMetrics.totalPlannedHours} <Typography component="span" variant="caption" color="text.secondary">hrs</Typography>
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Actual Hours Logged</Typography>
                        <Typography variant="h6" fontWeight="bold" color={hoursMetrics.isHoursOverrun ? "error.main" : "text.primary"}>
                          {hoursMetrics.totalActualHours} <Typography component="span" variant="caption" color="text.secondary">hrs</Typography>
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Effort Variance</Typography>
                        <Typography variant="h6" fontWeight="bold" color={hoursMetrics.isHoursOverrun ? "warning.dark" : "success.main"}>
                          {hoursMetrics.hoursVariance > 0 ? `+${hoursMetrics.hoursVariance}` : hoursMetrics.hoursVariance} <Typography component="span" variant="caption" color="text.secondary">hrs</Typography>
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Box sx={{ p: 1.5, bgcolor: '#ffffff', borderRadius: 1.5, border: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" color="text.secondary" display="block">Est. Cost Based on Hours</Typography>
                        <Typography variant="h6" fontWeight="bold" color="primary.main">
                          {companyConfig?.currency_symbol || '₹'}{Number(hoursMetrics.estimatedRevisedBudget || 0).toLocaleString()}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  {hoursMetrics.isHoursOverrun && (
                    <Alert
                      severity="warning"
                      sx={{ alignItems: 'center' }}
                      action={
                        <Button
                          color="inherit"
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 'bold', textTransform: 'none', whiteSpace: 'nowrap', bgcolor: '#ffffff' }}
                          onClick={() => {
                            setProjectData(prev => ({
                              ...prev,
                              timeline: {
                                ...prev.timeline,
                                budget: String(hoursMetrics.estimatedRevisedBudget),
                                reason: prev.timeline.reason || `Budget revised to ${companyConfig?.currency_symbol || '₹'}${Number(hoursMetrics.estimatedRevisedBudget).toLocaleString()} to cover ${hoursMetrics.hoursVariance} additional hours logged (+${hoursMetrics.overrunPercentage}% effort overrun).`
                              }
                            }));
                          }}
                        >
                          Apply Revised Budget ({companyConfig?.currency_symbol || '₹'}{Number(hoursMetrics.estimatedRevisedBudget).toLocaleString()})
                        </Button>
                      }
                    >
                      <Typography variant="body2">
                        <strong>Budget Overrun Warning:</strong> Actual hours logged ({hoursMetrics.totalActualHours} hrs) have exceeded the planned estimate ({hoursMetrics.totalPlannedHours} hrs) across {hoursMetrics.totalIssuesCount} work items. In standard project governance, exceeding planned effort increases labor costs unless formal budget revision is recorded.
                      </Typography>
                    </Alert>
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            <CloudUpload sx={{ mr: 1, verticalAlign: "middle" }} />
            Additional Documentation (Optional)
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload additional project documentation or reference materials. For AI auto-fill, use the section above.
          </Typography>
          
          <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
            <input
              accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif"
              style={{ display: 'none' }}
              id="documentation-upload"
              multiple
              type="file"
              onChange={handleFileUpload}
            />
            <label htmlFor="documentation-upload">
              <Paper
                sx={{
                  p: 3,
                  width: "400px",
                  border: '2px dashed #ccc',
                  backgroundColor: '#fafafa',
                  cursor: 'pointer',
                  textAlign: 'center',
                  transition: 'all 0.3s',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: '#f0f7ff'
                  }
                }}
                component="div"
              >
                <CloudUpload sx={{ fontSize: 28, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Click to upload documentation
                </Typography>
              </Paper>
            </label>
          </Box>
          
          {uploadError && (
            <Box sx={{ mb: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
              <Typography color="error" variant="body2">{uploadError}</Typography>
            </Box>
          )}
          
          {uploadedFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                Uploaded Files ({uploadedFiles.length})
              </Typography>
              {uploadedFiles.map((file) => (
                <Paper key={file.id} sx={{ p: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
                  <Box sx={{ mr: 2 }}>
                    {getFileIcon(file.type)}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight="medium">
                      {file.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(file.size)}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleRemoveFile(file.id)}
                    title="Remove file"
                  >
                    <Delete />
                  </IconButton>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
        
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
          {!isEditMode && (
            <Button
              variant="outlined"
              color="primary"
              onClick={onBack}
              sx={{ mr: 2 }}
            >
              Back
            </Button>
          )}
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || isDocProcessing}
            startIcon={loading ? <CircularProgress size={20} /> : <Check />}
          >
            {isEditMode ? "Save Changes" : "Create Project"}
          </Button>
        </Box>
      </form>

      {/* Dialog for adding custom project type */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Add custom project type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Create a custom project type that fits your organization's needs.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Project type name"
            fullWidth
            variant="outlined"
            value={newProjectType}
            onChange={(e) => setNewProjectType(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handleAddProjectType} color="primary" variant="contained">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}